/**
 * Move-to-a-new-home checks and the sponsor notice.
 * The notice names the new city and state only.
 * No object spread: this file is required from both the app and functions.
 */

function clean(value, max) {
  return String(value == null ? '' : value)
    .replace(/[\r\n]+/g, ' ')
    .trim()
    .slice(0, max || 120);
}

/** Trim and uppercase first, then keep 6 characters. A pasted " rbdrs4 " is RBDRS4. */
function sponsorCodeValue(value) {
  return String(value == null ? '' : value)
    .replace(/[\r\n]+/g, ' ')
    .trim()
    .toUpperCase()
    .slice(0, 6);
}

function moveAddressInput(input) {
  const source = input || {};
  const address = clean(source.address, 200);
  const city = clean(source.city, 80);
  const stateRaw = clean(source.state, 20).toUpperCase();
  const state = /^[A-Z]{2}$/.test(stateRaw) ? stateRaw : '';
  const zip = String(source.zip == null ? '' : source.zip).replace(/\D/g, '').slice(0, 5);
  const countyFips = clean(source.countyFips || source.property_county_fips, 5);
  const countySource = clean(source.countySource || source.property_county_source, 20);
  const errors = [];
  if (address.length < 4) errors.push('Enter the street address.');
  if (!city) errors.push('Enter the city.');
  if (!/^[A-Z]{2}$/.test(state)) errors.push('Choose a state.');
  if (zip.length < 5) errors.push('Enter a 5-digit ZIP code.');
  if (source.countyPending) errors.push('Choose the county for this ZIP code.');
  return {
    address,
    city,
    state,
    zip,
    countyFips,
    countySource,
    errors,
  };
}

function moveSponsorPlan(choice, inviteCode) {
  const sponsor = choice === 'invite' || choice === 'sponsorless' ? choice : 'keep';
  const code = sponsorCodeValue(inviteCode);
  if (sponsor === 'invite' && !/^[A-Z0-9]{6}$/.test(code)) {
    return { status: 400, error: 'Enter the 6-character sponsor code.' };
  }
  return { sponsor, inviteCode: sponsor === 'invite' ? code : '' };
}

/**
 * Email and in-app copy. `street` is ignored on purpose so a caller
 * cannot put the new address in the sponsor notice.
 */
function movingNoticeCopy(input) {
  const source = input || {};
  const who = clean(source.homeownerName, 80) || 'A homeowner';
  const city = clean(source.city, 80);
  const state = clean(source.state, 2).toUpperCase();
  const place = city && state ? `${city}, ${state}` : (city || state);
  const where = place ? ` The new home is in ${place}.` : '';
  const kept = source.kept !== false;
  const follow = kept
    ? 'You still sponsor the new home unless you release them. Keeping them is the default.'
    : 'They did not keep you as the sponsor on the new home.';
  return {
    subject: `${who} is moving`,
    paragraphs: [
      `${who} is moving to a new home.${where}`,
      follow,
    ],
  };
}

function moveOutcomeMessage(outcome) {
  if (outcome === 'invalid_address') return { status: 400, error: 'Enter the street, city, state, and ZIP code.' };
  if (outcome === 'same_home') return { status: 400, error: 'That is already the address on this home.' };
  if (outcome === 'invalid_sponsor') return { status: 400, error: 'Choose how this home is sponsored.' };
  if (outcome === 'invite_not_found') return { status: 400, error: 'That sponsor code is not active.' };
  if (outcome === 'not_found') return { status: 404, error: 'That home is not current.' };
  if (outcome === 'archived') return { status: 403, error: 'This home is archived and can only be viewed.' };
  if (outcome === 'limit_reached') return { status: 409, error: 'That sponsor has no open homeowner seats.' };
  if (outcome === 'subscription_inactive') return { status: 409, error: 'That sponsor\'s plan is not active.' };
  if (outcome === 'same_agent') return { status: 200, error: '' };
  return null;
}

module.exports = {
  sponsorCodeValue,
  moveAddressInput,
  moveSponsorPlan,
  movingNoticeCopy,
  moveOutcomeMessage,
};
